"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.calculateWeightedScore = calculateWeightedScore;
exports.didAnalyticsDocumentPass = didAnalyticsDocumentPass;
exports.generateBasePerformanceScore = generateBasePerformanceScore;
exports.getAnalyticsOverview = getAnalyticsOverview;
exports.getMissionPerformanceScores = getMissionPerformanceScores;
exports.getPerformanceScoresForStudent = getPerformanceScoresForStudent;
exports.getQuestionsAnswerStats = getQuestionsAnswerStats;
exports.getStudentQuestionAnswerStats = getStudentQuestionAnswerStats;
exports.getTotalMissionScore = getTotalMissionScore;
exports.sumPointsObject = sumPointsObject;
var _functions = require("../utility/functions");
/**
 * getAnalyticsOverview - get the overview of the analytics
 *
 * @param {?ClassStudentAnalytics} classAnalytics - the analytics to get the overview of
 *
 * @return {?AnalyticsOverview} - the overview of the analytics
 */
function getAnalyticsOverview(classAnalytics) {
  if (!classAnalytics) {
    return null;
  }
  let finalScore = getTotalMissionScore(classAnalytics);
  let missionStatistics = classAnalytics.missionStatistics || {};
  const studentsWhoParticipated = classAnalytics?.activeStudents || 0;
  const questionStats = getQuestionsAnswerStats(classAnalytics);
  return {
    missionStatistics: {
      ...missionStatistics,
      questions: {
        score: questionStats.questionsCorrect,
        outOf: questionStats.questionsGraded
      }
    },
    finalScore,
    averageStudentScore: finalScore != null && studentsWhoParticipated ? finalScore / studentsWhoParticipated : null,
    MVPs: classAnalytics?.MVPs || []
  };
}

/**
 * getTotalMissionScore - get the total amount of points gathered during the mission
 *
 * @param {?ClassStudentAnalytics} classAnalytics - the analytics for the mission
 *
 * @return {number} - the amount of points gathered during the mission
 */
function getTotalMissionScore(classAnalytics) {
  if (!classAnalytics) {
    return 0;
  }
  let totalScore = 0;
  const crewPoints = sumPointsObject(classAnalytics.extraCrewPoints);
  const studentAnalytics = classAnalytics.studentAnalytics;
  if (studentAnalytics) {
    Object.keys(studentAnalytics).forEach(studentId => {
      totalScore += sumPointsObject(studentAnalytics[studentId].studentPoints) + crewPoints;
    });
  }
  return totalScore;
}

/**
 * sumPointsObject - get the number of points from a points object
 *
 * @param {?StudentPoints} pointsObject - the points object to get the number of points from
 *
 * @return {number} - the total number of points in the points object
 */
function sumPointsObject(pointsObject) {
  let points = 0;
  if (!pointsObject) {
    return 0;
  }
  Object.keys(pointsObject).forEach(category => {
    points += pointsObject[category];
  });
  return points;
}

/**
 * getQuestionsAnswerStats - get statistics about all the student answers in the analytics
 *
 * @param {?ClassStudentAnalytics} classAnalytics - the analytics to get the question statistics from
 *
 * @return {AnalyticsQuestionAnswerStats} - statistics about the question answers
 */
function getQuestionsAnswerStats(classAnalytics) {
  let questionsAnswered = 0;
  let questionsGraded = 0;
  let questionsCorrect = 0;
  const studentAnalytics = classAnalytics?.studentAnalytics;
  if (studentAnalytics) {
    Object.keys(studentAnalytics).forEach(studentId => {
      const studentQuestionStats = getStudentQuestionAnswerStats(studentAnalytics[studentId]);
      questionsAnswered += studentQuestionStats.questionsAnswered;
      questionsGraded += studentQuestionStats.questionsGraded;
      questionsCorrect += studentQuestionStats.questionsCorrect;
    });
  }
  return {
    questionsAnswered,
    questionsGraded,
    questionsCorrect
  };
}

/**
 * getQuestionsAnswerStats - get statistics about a single student from an analytics object
 *
 * @param {?StudentAnalytics} studentAnalytics - the analytics to get the student question stats from
 *
 * @return {AnalyticsQuestionAnswerStats} - statistics about the question answers for the student
 */
function getStudentQuestionAnswerStats(studentAnalytics) {
  let questionsAnswered = 0;
  let questionsGraded = 0;
  let questionsCorrect = 0;
  const studentAnswers = studentAnalytics?.questionAnswers;
  if (studentAnswers) {
    questionsAnswered += studentAnswers.length;
    studentAnswers.forEach(answer => {
      if (!answer.score) {
        return;
      }
      const {
        score,
        perfectScore
      } = answer.score;
      if (score === null) {
        return;
      }
      questionsGraded += 1;
      if (score === perfectScore) {
        questionsCorrect += 1;
      }
    });
  }
  return {
    questionsAnswered,
    questionsGraded,
    questionsCorrect
  };
}
/**
 * generateBasePerformanceScore - generate a default Performance Score object
 *
 * @return {PerformanceScores}
 */
function generateBasePerformanceScore() {
  return {
    knowledge: {
      questions: null,
      application: null
    },
    skills: {
      collaboration: null,
      criticalThinking: null
    },
    disposition: {
      grit: null,
      initiative: null
    }
  };
}

/**
 * calculateWeightedScore - calculate a weighted score (multiplied by 100 to convert between proportion and percentage) for the given performance itemCollection
 *
 * @param {?PerformanceItemCollection} itemCollection - the item collection (such as `studentAnalytics.skills.collaboration`)
 * @param {{[subCategory:string]:number}} categoryWeights - the weights for the category of the item collection (such as `weights.collaboration`)
 *
 * @return {?number} - the weighted mean
 */
function calculateWeightedScore(itemCollection, categoryWeights) {
  if (!itemCollection) {
    return null;
  }
  const weights = categoryWeights || {};
  let total = 0;
  let totalWeight = 0;
  Object.keys(itemCollection).forEach(itemKey => {
    const value = itemCollection[itemKey];
    if (value == null) {
      return;
    }
    const weight = weights[itemKey] ?? 1;
    if (typeof value === 'number') {
      total += value * weight;
      totalWeight += weight;
    } else if (value && typeof value === 'object' && typeof value.score === 'number') {
      total += value.score * weight;
      totalWeight += weight;
    }
  });
  const score = total / totalWeight;
  return isFinite(score) ? score * 100 : null;
}

/**
 * getPerformanceScoresForStudent - get the Performance scores for a student
 *
 * @param {ClassStudentAnalytics} classStudentAnalytics - the class analytics
 * @param {string} studentId - the id of the student to calculate the Performance scores for
 *
 * @return {PerformanceScores} - the performance scores for the student (all values will be null if student does not exist in the analytics)
 */
function getPerformanceScoresForStudent(classStudentAnalytics, studentId) {
  const weights = classStudentAnalytics.weights;
  const studentAnalytics = classStudentAnalytics.studentAnalytics?.[studentId];
  if (studentAnalytics) {
    const questionsScore = studentAnalytics.knowledge?.questions;
    const applicationScore = studentAnalytics.knowledge?.application;
    const collaborationScore = calculateWeightedScore(studentAnalytics.skills?.collaboration, weights?.collaboration);
    const criticalThinkingScore = calculateWeightedScore(studentAnalytics.skills?.criticalThinking, weights?.criticalThinking);
    const gritScore = calculateWeightedScore(studentAnalytics.disposition?.grit, weights?.grit);
    const initiativeScore = calculateWeightedScore(studentAnalytics.disposition?.initiative, weights?.initiative);
    return {
      /**
       *
       * ONLY CHANGE THESE IF YOU ARE MAKING CHANGES TO THE MISSION SERVER ANALYTICS CREATION
       *
       */
      knowledge: {
        questions: questionsScore && questionsScore * 100,
        application: applicationScore && applicationScore * 100
      },
      skills: {
        collaboration: collaborationScore,
        criticalThinking: criticalThinkingScore
      },
      disposition: {
        grit: gritScore,
        initiative: initiativeScore
      }
    };
  } else {
    return generateBasePerformanceScore();
  }
}

/**
 * getMissionPerformanceScores - get the combined (by mean) Performance scores of the entire class
 *
 * @param {?ClassStudentAnalytics} classAnalytics - the analytics for the mission
 *
 * @return {PerformanceScores}
 */
function getMissionPerformanceScores(classAnalytics) {
  const studentAnalytics = classAnalytics?.studentAnalytics;
  const studentPerformanceScores = classAnalytics && studentAnalytics ? Object.keys(studentAnalytics).map(studentId => getPerformanceScoresForStudent(classAnalytics, studentId)) : [];
  const calculateMeanScore = selector => (0, _functions.mean)(studentPerformanceScores.map(selector));
  const missionPerformanceScores = generateBasePerformanceScore();
  Object.keys(missionPerformanceScores).forEach(category => Object.keys(missionPerformanceScores[category]).forEach(subCategory =>
  // $FlowIgnore[prop-missing] - subcategory exists in the category
  missionPerformanceScores[category][subCategory] = calculateMeanScore(
  // $FlowIgnore[prop-missing] - subcategory exists in the category
  scores => scores[category]?.[subCategory])));
  return missionPerformanceScores;
}

/**
 * didAnalyticsDocumentPass - determine if a document passed
 *
 * @param  {?CollaborativeCulminatingMomentAnalyticsScore} documentGrading - the grading from the teacher for the document
 * @param  {CreativeCanvasStationIssue} issue - the issue the document was graded for
 *
 * @returns boolean - true if the document passed, false otherwise
 */
function didAnalyticsDocumentPass(documentGrading, issue) {
  let score = 0;
  let allRequiredPassed = issue.rubric.criteria.every(criteria => {
    const maxOptionScore = Math.max(0, ...criteria.gradingOptions.filter(({
      correct
    }) => correct).map(({
      score
    }) => score));
    const selectedOption = documentGrading?.criteriaId[criteria.id]?.selectedOption;
    if (!selectedOption) {
      // optimistic look ahead
      score += maxOptionScore;
      return true;
    }
    const selectedOptionFromCanvas = criteria.gradingOptions.find(option => option.id === selectedOption);
    if (!selectedOptionFromCanvas) {
      // optimistic look ahead
      score += maxOptionScore;
      return true;
    }
    score += selectedOptionFromCanvas.score;
    if (criteria.required) {
      return selectedOptionFromCanvas.correct;
    }
    return true;
  });
  return allRequiredPassed && score >= (issue.rubric.requiredScore ??
  // while requiredScore should always be populated during analytics, in case it is not, consider the passing score the max possible score
  issue.rubric.criteria.reduce((maxPossibleScore, criteria) => {
    return maxPossibleScore + Math.max(0, ...criteria.gradingOptions.filter(({
      correct
    }) => correct).map(({
      score
    }) => score));
  }, 0));
}